import { Button, Grid, Stack } from "@mui/material";
import { CustomTextView } from "../../../component/CustomTextView";
import { CheckCircleRounded } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import {
  COLOR_GREEN,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { useTranslation } from "react-i18next";
import { Category } from "../../../../domain/model/Category";
import { CustomImageView } from "../../../component/CustomImageView";
import { CustomCardView } from "../../../component/CustomCardView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { CustomPopoverView } from "../../../component/CustomPopoverView";
import { ChooseCategoryDetailsView } from "./ChooseCategoryDetailsView";
import { LoadingView } from "../../../component/LoadingView";
import { Player } from "../../../../domain/model/User";

export function ChooseCategoriesView({
  player,
  categories,
  selectedCategory,
  setSelectedCategory,
  loading,
  onCategoryClicked,
}: {
  player: Player;
  categories: Array<Category>;
  selectedCategory: Category | undefined;
  setSelectedCategory: (category: Category) => void;
  loading: boolean;
  onCategoryClicked: (category: Category, closePopover?: () => void) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";
  const { width } = useWindowDimensions();
  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const [categoriesNumbersUpdated, setCategoriesNumbersUpdated] =
    useState(false);

  useEffect(() => {
    if (!categoriesNumbersUpdated && categories && selectedCategory) {
      setSelectedCategory(
        categories.filter((c) => c.id == selectedCategory.id)[0],
      );
      setCategoriesNumbersUpdated(true);
    }
  }, [categories]);

  return (
    <Stack
      spacing={2}
      width={"100%"}
      height={"100%"}
      sx={{ overflow: "auto" }}
      p={width < 700 ? 1.5 : 2}
    >
      <Stack>
        <CustomTextView
          text={t("choose_max_6_categories")}
          fontWeight="bold"
          variant="h6"
        />
        <CustomTextView
          text={t(
            "double_tap_on_each_category_to_select_all_its_subcategories",
          )}
          variant="subtitle2"
          textColor={COLOR_LIGHT_GRAY}
        />
      </Stack>

      {loading ? (
        <LoadingView minHeight="50vh" />
      ) : (
        <Grid container sx={{ rowGap: 1 }}>
          {categories.map((category, index) => {
            return (
              <Grid
                size={6}
                sx={{
                  pr: index % 2 == 0 ? (isRtl ? 0 : 0.5) : isRtl ? 0.5 : 0,
                  pl: index % 2 != 1 ? (isRtl ? 0.5 : 0) : isRtl ? 0 : 0.5,
                }}
                key={category.id}
              >
                <CategoryItemView
                  category={category}
                  player={player}
                  onCategoryClicked={onCategoryClicked}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {selectedCategory && (
        <CustomPopoverView
          onClose={() => {
            setAnchorElPosition(null);
          }}
          anchorElPosition={anchorElPosition}
          layout={
            <>
              <ChooseCategoryDetailsView
                category={selectedCategory!}
                onCategoryClicked={onCategoryClicked}
                onPopoverClose={() => setAnchorElPosition(null)}
              />
            </>
          }
        />
      )}
    </Stack>
  );
}

function CategoryItemView({
  category,
  onCategoryClicked,
  player,
}: {
  category: Category;
  player: Player;
  onCategoryClicked: (category: Category) => void;
}) {
  const { width } = useWindowDimensions();
  const isMobile = width < 900;
  return (
    <CustomCardView
      padding={0}
      height={isMobile ? 160 : 200}
      background={COLOR_WHITE}
      border={
        !category.isSelected ? "solid 1px black" : `solid 4px ${COLOR_GREEN}`
      }
      children={
        <Button
          sx={{ p: 0, textTransform: "none", height: "100%" }}
          onClick={() => onCategoryClicked(category)}
          fullWidth
        >
          <Stack
            spacing={0}
            height={"100%"}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack sx={{ height: "100%", width: "100%", position: "relative" }}>
              <CustomImageView
                src={category.imageUrl}
                width={"100%"}
                height={"100%"}
              />
            </Stack>

            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              justifyContent={"center"}
              width={"100%"}
              bgcolor={COLOR_MAIN}
              position={"absolute"}
              bottom={0}
              p={0.5}
            >
              <CustomTextView
                text={category.title}
                variant="body1"
                fontWeight="bold"
                textColor="white"
              />
              {category.isSelected && (
                <CheckCircleRounded sx={{ color: COLOR_GREEN }} />
              )}
            </Stack>
          </Stack>
        </Button>
      }
    />
  );
}
